import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Phone, MapPin, Mail, Calendar, CheckCircle, XCircle, ArrowLeft, AlertTriangle, Edit, Save, Building, Briefcase, Clock } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StaffRoleControl } from '@/components/internal-staff/StaffAccountControls';
import { canManageInternal, INTERNAL_ROLE } from '@/components/internal-staff/shared';
import { EmployeeSiteAssignmentNotice, siteAssignmentError } from '@/components/EmployeeSiteAssignmentNotice';

const AdminEmployeeProfile = ({ user, activeModule = "b2b" }) => {
  const { userId } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [siteAssignmentDraft, setSiteAssignmentDraft] = useState('');
  const [siteAssignmentReason, setSiteAssignmentReason] = useState('');
  const [siteAssignmentSaving, setSiteAssignmentSaving] = useState(false);
  const [siteAssignmentFeedback, setSiteAssignmentFeedback] = useState('');
  const [siteAssignmentIssue, setSiteAssignmentIssue] = useState(null);

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    setSiteAssignmentIssue(null);
    setEmployee(null);
    try {
      // Fetch employee data first - this is required
      const empRes = await axios.get(`${API}/users/${encodeURIComponent(userId)}`, { withCredentials: true });
      setEmployee(empRes.data);
      
      setFormData({
        name: empRes.data.name || '',
        email: empRes.data.email || '',
        first_name: empRes.data.first_name || '',
        last_name: empRes.data.last_name || '',
        phone: empRes.data.phone || '',
        address: empRes.data.address || '',
        status: empRes.data.status || 'pending',
        active: empRes.data.active !== false,
        contract_hours: empRes.data.contract_hours || '',
        work_pattern: empRes.data.work_pattern || 'standard',
        can_view_work_hours: empRes.data.can_view_work_hours === true
      });

      setSiteAssignmentDraft(empRes.data.site_id || '');
      setSiteAssignmentReason('');
      setSiteAssignmentFeedback('');
      
      // Fetch sites - optional
      try {
        const sitesRes = await axios.get(`${API}/sites`, { withCredentials: true });
        setSites(sitesRes.data || []);
      } catch (err) {
        console.error('Failed to fetch sites:', err);
        setSites([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch employee profile:', error);
      const status = error.response?.status;
      setLoadError(status === 404 ? 'Ce profil salarié est introuvable.' : status === 403 ? 'Vous ne disposez pas des droits pour consulter ce profil.' : 'Impossible de charger le profil de cet employé. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update profile info
      await axios.put(`${API}/users/${userId}/profile`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        address: formData.address,
        profile_photo: formData.profile_photo
      }, { withCredentials: true });
      
      // Update user info (name, email, site, status, active, contract_hours)
      await axios.put(`${API}/users/${userId}`, {
        name: formData.name,
        email: formData.email,
        status: formData.status,
        active: formData.active,
        contract_hours: formData.contract_hours || null,
        work_pattern: formData.work_pattern || 'standard',
        can_view_work_hours: formData.can_view_work_hours === true
      }, { withCredentials: true });
      
      setIsEditing(false);
      await fetchData();
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: employee.name || '',
      email: employee.email || '',
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      phone: employee.phone || '',
      address: employee.address || '',
      status: employee.status || 'pending',
      active: employee.active !== false,
      profile_photo: employee.profile_photo || null,
      contract_hours: employee.contract_hours || '',
      work_pattern: employee.work_pattern || 'standard',
      can_view_work_hours: employee.can_view_work_hours === true
    });
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="text-center py-12">
          <p role="alert" className="text-red-700 mb-4" data-testid="employee-profile-load-error">{loadError || 'Employé non trouvé'}</p>
          <div className="flex justify-center gap-3"><Button onClick={fetchData} data-testid="employee-profile-retry">Réessayer</Button><Link to="/admin/b2b/employees" className="p-2 underline" data-testid="employee-profile-error-back">Retour aux employés</Link></div>
        </div>
      </Layout>
    );
  }

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site?.name || 'Non assigne';
  };

  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const canManageSiteAssignment = user?.is_super_admin === true || permissions.includes('employee_site_assignment_manage');

  const applySiteAssignment = async () => {
    setSiteAssignmentSaving(true);
    setSiteAssignmentFeedback('');
    setSiteAssignmentIssue(null);
    try {
      const payload = {
        site_id: siteAssignmentDraft || null,
        reason: siteAssignmentReason || null,
        source: 'admin_employees_tab'
      };

      const res = await axios.put(`${API}/employees/${userId}/site-assignment`, payload, { withCredentials: true });
      await fetchData();
      setSiteAssignmentFeedback(res.data?.action === 'remove' ? 'Rattachement au site supprimé.' : 'Rattachement au site enregistré.');
    } catch (error) {
      setSiteAssignmentIssue(siteAssignmentError(error));
    } finally {
      setSiteAssignmentSaving(false);
    }
  };

  const cancelSiteAssignment = () => {
    setSiteAssignmentDraft(employee.site_id || '');
    setSiteAssignmentReason('');
    setSiteAssignmentIssue(null);
    setSiteAssignmentFeedback('');
  };

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 fade-in max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/admin/employees">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-[#0F172A]" data-testid="employee-profile-title">
              Profil de {employee.name}
            </h1>
            <p className="text-gray-500">Modification complete du profil</p>
          </div>
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)} className="btn-primary" data-testid="employee-profile-edit">
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="employee-profile-save">
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
              <Button onClick={handleCancel} variant="outline" disabled={saving}>
                Annuler
              </Button>
            </div>
          )}
        </div>

        {/* Basic Information */}
        {canManageInternal(user) && ['employee', INTERNAL_ROLE].includes(employee.role) && <StaffRoleControl key={`${employee.user_id}-${employee.role}`} employee={employee} onSaved={fetchData} />}
        <Card>
          <CardHeader>
            <CardTitle>Informations de base</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" />
                    Nom complet
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nom complet"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" />
                    Prenom
                  </Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="Prenom"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" />
                    Nom de famille
                  </Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Nom de famille"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4" />
                    Telephone
                  </Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="06 12 34 56 78"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Building className="w-4 h-4" />
                    Site / Société (géré dans la section dédiée)
                  </Label>
                  <Input value={getSiteName(employee.site_id)} disabled data-testid="employee-current-site-readonly-input" />
                </div>
                <div className="md:col-span-2">
                  <Label className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4" />
                    Adresse
                  </Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Adresse complete"
                  />
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Nom complet</p>
                  <p className="font-semibold">{employee.name || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-semibold">{employee.email || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Prenom</p>
                  <p className="font-semibold">{employee.first_name || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Nom de famille</p>
                  <p className="font-semibold">{employee.last_name || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Telephone</p>
                  <p className="font-semibold">{employee.phone || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Site / Societe</p>
                  <p className="font-semibold">{getSiteName(employee.site_id)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Adresse</p>
                  <p className="font-semibold">{employee.address || '-'}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="employee-site-assignment-card">
          <CardHeader>
            <CardTitle>Rattachement du salarié au site</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg" data-testid="employee-site-current-container">
                <p className="text-sm text-slate-500 mb-1">Site actuel</p>
                <p className="font-semibold" data-testid="employee-site-current-value">{getSiteName(employee.site_id)}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg" data-testid="employee-site-target-container">
                <Label className="text-sm text-slate-500 mb-2 block" htmlFor="employee-site-target-select">Nouveau site</Label>
                <select
                  id="employee-site-target-select"
                  value={siteAssignmentDraft}
                  onChange={(e) => setSiteAssignmentDraft(e.target.value)}
                  className="w-full p-2 border rounded-md"
                  disabled={!canManageSiteAssignment || siteAssignmentSaving}
                  data-testid="employee-site-target-select"
                >
                  <option value="">Non assigné</option>
                  {sites.map((site) => (
                    <option key={site.site_id} value={site.site_id}>{site.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="employee-site-reason" className="mb-2 block">Motif (optionnel)</Label>
              <Input
                id="employee-site-reason"
                value={siteAssignmentReason}
                onChange={(e) => setSiteAssignmentReason(e.target.value)}
                placeholder="Ex: changement d'équipe / transfert site"
                disabled={!canManageSiteAssignment || siteAssignmentSaving}
                data-testid="employee-site-reason-input"
              />
            </div>

            {!canManageSiteAssignment && (
              <p className="text-sm text-amber-700" data-testid="employee-site-permission-warning">
                Vous n&apos;avez pas la permission de modifier le rattachement du site.
              </p>
            )}

            {siteAssignmentFeedback && (
              <p role="status" className="text-sm text-emerald-700" data-testid="employee-site-feedback-success">{siteAssignmentFeedback}</p>
            )}

            <EmployeeSiteAssignmentNotice issue={siteAssignmentIssue} busy={siteAssignmentSaving} onCancel={cancelSiteAssignment} onReload={fetchData} />

            <div className="flex justify-end">
              <Button
                onClick={() => applySiteAssignment()}
                disabled={!canManageSiteAssignment || siteAssignmentSaving}
                data-testid="employee-site-save-button"
              >
                {siteAssignmentSaving ? 'Enregistrement...' : 'Enregistrer le rattachement site'}
              </Button>
            </div>

          </CardContent>
        </Card>

        {/* Account Status */}
        <Card>
          <CardHeader>
            <CardTitle>Statut du compte</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-4 h-4" />
                    Statut
                  </Label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="pending">En attente</option>
                    <option value="approved">Approuve</option>
                    <option value="rejected">Rejete</option>
                  </select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    Compte actif
                  </Label>
                  <select
                    value={formData.active ? 'true' : 'false'}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'true' }))}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="true">Actif</option>
                    <option value="false">Desactive</option>
                  </select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4" />
                    Type de contrat (heures/sem)
                  </Label>
                  <select
                    value={formData.contract_hours || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_hours: e.target.value }))}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Non défini</option>
                    <option value="50">50h</option>
                    <option value="45">45h</option>
                    <option value="40">40h</option>
                    <option value="39">39h</option>
                    <option value="35">35h</option>
                    <option value="30">30h</option>
                    <option value="25">25h</option>
                    <option value="20">20h</option>
                    <option value="15">15h</option>
                    <option value="10">10h</option>
                  </select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-4 h-4" />
                    Profil de travail
                  </Label>
                  <select
                    value={formData.work_pattern || 'standard'}
                    onChange={(e) => setFormData(prev => ({ ...prev, work_pattern: e.target.value }))}
                    className="w-full p-2 border rounded-md"
                    data-testid="employee-work-pattern-select"
                  >
                    <option value="standard">Standard</option>
                    <option value="weekend_only">Week-end uniquement</option>
                  </select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2" data-testid="employee-hours-visibility-label">
                    <Clock className="w-4 h-4" />
                    Consultation des heures salarié
                  </Label>
                  <button
                    type="button"
                    className={`w-full h-10 rounded-md border px-3 text-sm font-medium flex items-center justify-between ${formData.can_view_work_hours ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-300 text-slate-700'}`}
                    onClick={() => setFormData(prev => ({ ...prev, can_view_work_hours: !prev.can_view_work_hours }))}
                    data-testid="employee-hours-visibility-toggle"
                  >
                    <span>{formData.can_view_work_hours ? 'Autorisé' : 'Non autorisé'}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${formData.can_view_work_hours ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-200 text-slate-700'}`} data-testid="employee-hours-visibility-toggle-state">
                      {formData.can_view_work_hours ? 'ON' : 'OFF'}
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Statut</p>
                  <div className="flex items-center gap-2">
                    {employee.status === 'approved' && <CheckCircle className="w-5 h-5 text-green-500" />}
                    {employee.status === 'pending' && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                    {employee.status === 'rejected' && <XCircle className="w-5 h-5 text-red-500" />}
                    <span className="font-semibold">
                      {employee.status === 'approved' && 'Approuve'}
                      {employee.status === 'pending' && 'En attente'}
                      {employee.status === 'rejected' && 'Rejete'}
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Compte</p>
                  <div className="flex items-center gap-2">
                    {employee.active !== false ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-semibold text-green-700">Actif</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="font-semibold text-red-700">Desactive</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Type de contrat</p>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold">{employee.contract_hours ? `${employee.contract_hours}h/sem` : 'Non défini'}</span>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Profil de travail</p>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-cyan-500" />
                    <span className="font-semibold">{employee.work_pattern === 'weekend_only' ? 'Week-end uniquement' : 'Standard'}</span>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Date inscription</p>
                  <p className="font-semibold">
                    {employee.created_at ? new Date(employee.created_at).toLocaleDateString('fr-FR') : '-'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg" data-testid="employee-hours-visibility-readonly-card">
                  <p className="text-sm text-gray-500 mb-1">Consultation des heures</p>
                  <div className="flex items-center gap-2">
                    <Clock className={`w-5 h-5 ${employee.can_view_work_hours === true ? 'text-emerald-500' : 'text-slate-400'}`} />
                    <span className={`font-semibold ${employee.can_view_work_hours === true ? 'text-emerald-700' : 'text-slate-600'}`} data-testid="employee-hours-visibility-readonly-state">
                      {employee.can_view_work_hours === true ? 'ON (autorisé)' : 'OFF (non autorisé)'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminEmployeeProfile;
